import React from "react";
import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Logo from "../../components/Logo";
import { Car, MessageSquare, LogOut, Plus, ExternalLink, Upload, ClipboardList, FolderOpen, Calculator, TrendingUp } from "lucide-react";

const links = [
  { to: "/admin", label: "Stock", icon: Car, end: true },
  { to: "/admin/inquiries", label: "Aanvragen", icon: MessageSquare },
  { to: "/admin/dossiers", label: "Dossiers", icon: FolderOpen },
  { to: "/admin/register", label: "Register", icon: ClipboardList },
  { to: "/admin/bod-calculator", label: "Bod calculator", short: "Bod", icon: Calculator },
  { to: "/admin/winst", label: "Winst & verkoop", short: "Winst", icon: TrendingUp },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white md:flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 bg-[#141414] border-r border-white/5 flex-col">
        <div className="px-6 py-6 border-b border-white/5">
          <Link to="/">
            <Logo />
          </Link>
        </div>
        <nav className="flex-1 py-6 space-y-1 px-3">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded text-sm transition-colors ${
                  isActive ? "bg-[#c5a76f]/10 text-[#c5a76f]" : "text-white/70 hover:bg-white/[0.04] hover:text-white"
                }`
              }
            >
              <l.icon className="w-4 h-4" />
              {l.label === "Import" ? "AS24 import" : l.label}
            </NavLink>
          ))}
          <NavLink
            to="/admin/cars/new"
            data-testid="admin-new-car-link"
            className="flex items-center gap-3 px-4 py-2.5 mt-4 rounded bg-[#c5a76f] text-black text-sm font-medium hover:bg-[#d8bd86] transition-colors"
          >
            <Plus className="w-4 h-4" /> Nieuwe auto
          </NavLink>
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 mb-4">
            {user?.picture && <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{user?.name}</div>
              <div className="text-xs text-white/40 truncate">{user?.email}</div>
            </div>
          </div>
          <a
            href="/"
            className="flex items-center gap-2 px-3 py-2 text-xs text-white/60 hover:text-[#c5a76f] mb-1"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Website bekijken
          </a>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 px-3 py-2 text-xs text-white/60 hover:text-red-400 w-full text-left"
          >
            <LogOut className="w-3.5 h-3.5" /> Afmelden
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 bg-[#141414]/95 backdrop-blur border-b border-white/5">
        <Link to="/admin" className="flex items-center">
          <Logo />
        </Link>
        <div className="flex items-center gap-2">
          <NavLink
            to="/admin/cars/new"
            data-testid="admin-new-car-mobile-button"
            className="flex items-center justify-center w-9 h-9 rounded-full bg-[#c5a76f] text-black active:scale-95 transition-transform"
            aria-label="Nieuwe auto"
          >
            <Plus className="w-5 h-5" />
          </NavLink>
          <button
            onClick={handleLogout}
            data-testid="admin-mobile-logout"
            className="flex items-center justify-center w-9 h-9 rounded-full bg-white/5 text-white/70 active:scale-95 transition-transform"
            aria-label="Afmelden"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-x-hidden pb-[calc(72px+env(safe-area-inset-bottom))] md:pb-0">
        <Outlet />
      </main>

      {/* Mobile bottom navigation */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#141414]/95 backdrop-blur border-t border-white/10 pb-[env(safe-area-inset-bottom)]"
        data-testid="admin-bottom-nav"
      >
        <div className="flex h-[72px] overflow-x-auto no-scrollbar">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `flex-1 min-w-[52px] flex flex-col items-center justify-center gap-1 transition-colors active:scale-95 ${
                  isActive ? "text-[#c5a76f]" : "text-white/55"
                }`
              }
              data-testid={`admin-nav-${l.label.toLowerCase()}`}
            >
              <l.icon className="w-5 h-5" strokeWidth={2} />
              <span className="text-[9px] uppercase font-medium whitespace-nowrap">{l.short || l.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
